import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Languages, ArrowLeftRight, Copy, Check, Volume2, Loader2,
  Utensils, Building2, Siren, Bus, ShoppingBag, MessageCircle, ArrowLeft, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LANGUAGES = [
  { code: 'Italian', flag: '🇮🇹', label: 'Italiano' },
  { code: 'English', flag: '🇬🇧', label: 'English' },
  { code: 'Spanish', flag: '🇪🇸', label: 'Español' },
  { code: 'French', flag: '🇫🇷', label: 'Français' },
  { code: 'German', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'Portuguese', flag: '🇵🇹', label: 'Português' },
  { code: 'Polish', flag: '🇵🇱', label: 'Polski' },
  { code: 'Russian', flag: '🇷🇺', label: 'Русский' },
  { code: 'Japanese', flag: '🇯🇵', label: '日本語' },
  { code: 'Chinese', flag: '🇨🇳', label: '中文' },
  { code: 'Korean', flag: '🇰🇷', label: '한국어' },
  { code: 'Arabic', flag: '🇸🇦', label: 'العربية' },
  { code: 'Hindi', flag: '🇮🇳', label: 'हिन्दी' },
  { code: 'Thai', flag: '🇹🇭', label: 'ไทย' },
  { code: 'Turkish', flag: '🇹🇷', label: 'Türkçe' },
  { code: 'Vietnamese', flag: '🇻🇳', label: 'Tiếng Việt' },
  { code: 'Dutch', flag: '🇳🇱', label: 'Nederlands' },
  { code: 'Greek', flag: '🇬🇷', label: 'Ελληνικά' },
  { code: 'Czech', flag: '🇨🇿', label: 'Čeština' },
  { code: 'Swedish', flag: '🇸🇪', label: 'Svenska' },
  { code: 'Indonesian', flag: '🇮🇩', label: 'Bahasa' },
  { code: 'Romanian', flag: '🇷🇴', label: 'Română' },
  { code: 'Hungarian', flag: '🇭🇺', label: 'Magyar' },
  { code: 'Croatian', flag: '🇭🇷', label: 'Hrvatski' },
];

const TITLES = {
  it: { title: 'Traduttore Istantaneo', subtitle: 'Traduci in qualsiasi lingua del mondo', inputPlaceholder: 'Scrivi il testo da tradurre...', translate: 'Traduci', copied: 'Copiato!', copy: 'Copia', phrases: 'Frasi Utili', powered: 'Alimentato da AI', from: 'Da', to: 'A', swap: 'Inverti', back: 'Indietro' },
  en: { title: 'Instant Translator', subtitle: 'Translate to any language in the world', inputPlaceholder: 'Type text to translate...', translate: 'Translate', copied: 'Copied!', copy: 'Copy', phrases: 'Quick Phrases', powered: 'AI Powered', from: 'From', to: 'To', swap: 'Swap', back: 'Back' },
  pl: { title: 'Tłumacz', subtitle: 'Tłumacz na dowolny język', inputPlaceholder: 'Wpisz tekst do tłumaczenia...', translate: 'Tłumacz', copied: 'Skopiowano!', copy: 'Kopiuj', phrases: 'Szybkie Frazy', powered: 'Napędzany AI', from: 'Z', to: 'Na', swap: 'Zamień', back: 'Wstecz' },
  fr: { title: 'Traducteur', subtitle: 'Traduisez dans toutes les langues', inputPlaceholder: 'Saisissez le texte à traduire...', translate: 'Traduire', copied: 'Copié !', copy: 'Copier', phrases: 'Phrases Utiles', powered: 'Propulsé par IA', from: 'De', to: 'Vers', swap: 'Inverser', back: 'Retour' },
  es: { title: 'Traductor', subtitle: 'Traduce a cualquier idioma', inputPlaceholder: 'Escribe el texto a traducir...', translate: 'Traducir', copied: '¡Copiado!', copy: 'Copiar', phrases: 'Frases Útiles', powered: 'Con IA', from: 'De', to: 'A', swap: 'Invertir', back: 'Atrás' },
  de: { title: 'Übersetzer', subtitle: 'Übersetze in jede Sprache', inputPlaceholder: 'Text zum Übersetzen eingeben...', translate: 'Übersetzen', copied: 'Kopiert!', copy: 'Kopieren', phrases: 'Nützliche Sätze', powered: 'KI-betrieben', from: 'Von', to: 'Nach', swap: 'Tauschen', back: 'Zurück' },
  ru: { title: 'Переводчик', subtitle: 'Переводите на любой язык', inputPlaceholder: 'Введите текст для перевода...', translate: 'Перевести', copied: 'Скопировано!', copy: 'Копировать', phrases: 'Полезные фразы', powered: 'На базе ИИ', from: 'С', to: 'На', swap: 'Поменять', back: 'Назад' },
};

const PHRASE_CATEGORIES = [
  { key: 'restaurant', icon: Utensils, color: 'text-amber-400', bg: 'bg-amber-500/10',
    phrases: {
      it: ['Il conto, per favore', 'Un tavolo per due', 'Sono allergico a...', 'Acqua naturale/frizzante', 'Il menù, per favore'],
      en: ['The bill, please', 'A table for two', 'I am allergic to...', 'Still/sparkling water', 'The menu, please'],
    }
  },
  { key: 'hotel', icon: Building2, color: 'text-sky-400', bg: 'bg-sky-500/10',
    phrases: {
      it: ['Ho una prenotazione', 'A che ora è il check-out?', 'La password del WiFi?', 'Posso avere un asciugamano?', 'Dov\'è l\'ascensore?'],
      en: ['I have a reservation', 'What time is check-out?', 'What is the WiFi password?', 'Can I have a towel?', 'Where is the elevator?'],
    }
  },
  { key: 'emergency', icon: Siren, color: 'text-rose-400', bg: 'bg-rose-500/10',
    phrases: {
      it: ['Ho bisogno di aiuto!', 'Chiamate un\'ambulanza', 'Dov\'è l\'ospedale più vicino?', 'Ho perso il passaporto', 'Chiamate la polizia'],
      en: ['I need help!', 'Call an ambulance', 'Where is the nearest hospital?', 'I lost my passport', 'Call the police'],
    }
  },
  { key: 'transport', icon: Bus, color: 'text-emerald-400', bg: 'bg-emerald-500/10',
    phrases: {
      it: ['Quanto costa il biglietto?', 'Mi porti a questo indirizzo', 'Dov\'è la stazione?', 'A che ora parte?', 'È lontano da qui?'],
      en: ['How much is the ticket?', 'Take me to this address', 'Where is the station?', 'What time does it leave?', 'Is it far from here?'],
    }
  },
  { key: 'shopping', icon: ShoppingBag, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10',
    phrases: {
      it: ['Quanto costa?', 'Posso provarlo?', 'Avete una taglia più grande?', 'Accettate carte di credito?', 'Posso avere uno sconto?'],
      en: ['How much does it cost?', 'Can I try it on?', 'Do you have a larger size?', 'Do you accept credit cards?', 'Can I get a discount?'],
    }
  },
  { key: 'general', icon: MessageCircle, color: 'text-teal-400', bg: 'bg-teal-500/10',
    phrases: {
      it: ['Buongiorno', 'Grazie mille', 'Non capisco', 'Parla inglese?', 'Dov\'è il bagno?'],
      en: ['Good morning', 'Thank you very much', 'I don\'t understand', 'Do you speak English?', 'Where is the bathroom?'],
    }
  },
];

const CAT_LABELS = {
  it: { restaurant: 'Ristorante', hotel: 'Hotel', emergency: 'Emergenze', transport: 'Trasporti', shopping: 'Shopping', general: 'Generale' },
  en: { restaurant: 'Restaurant', hotel: 'Hotel', emergency: 'Emergency', transport: 'Transport', shopping: 'Shopping', general: 'General' },
  pl: { restaurant: 'Restauracja', hotel: 'Hotel', emergency: 'Nagłe wypadki', transport: 'Transport', shopping: 'Zakupy', general: 'Ogólne' },
  fr: { restaurant: 'Restaurant', hotel: 'Hôtel', emergency: 'Urgences', transport: 'Transport', shopping: 'Shopping', general: 'Général' },
  es: { restaurant: 'Restaurante', hotel: 'Hotel', emergency: 'Emergencias', transport: 'Transporte', shopping: 'Compras', general: 'General' },
  de: { restaurant: 'Restaurant', hotel: 'Hotel', emergency: 'Notfall', transport: 'Transport', shopping: 'Einkaufen', general: 'Allgemein' },
  ru: { restaurant: 'Ресторан', hotel: 'Отель', emergency: 'Экстренные', transport: 'Транспорт', shopping: 'Покупки', general: 'Общее' },
};

function LanguageSelect({ value, onChange, label, testId }) {
  return (
    <div className="flex-1">
      <label className="block text-xs text-slate-500 mb-1.5 font-medium">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-sm text-white outline-none focus:border-teal-500/40 transition-colors appearance-none cursor-pointer"
        data-testid={testId}>
        {LANGUAGES.map(l => (
          <option key={l.code} value={l.code} className="bg-[#1a1f2e] text-white">
            {l.flag} {l.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function Translator({ embedded = false, onBack }) {
  const { i18n } = useTranslation();
  const lang = TITLES[i18n.language] ? i18n.language : 'en';
  const phraseLang = ['it', 'en'].includes(lang) ? lang : 'en';
  const T = TITLES[lang];
  const catLabels = CAT_LABELS[lang] || CAT_LABELS.en;

  const [sourceLang, setSourceLang] = useState('Italian');
  const [targetLang, setTargetLang] = useState('English');
  const [inputText, setInputText] = useState('');
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const doTranslate = useCallback(async (text, src, tgt) => {
    if (!text.trim()) { setTranslated(''); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/translate/text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source_lang: src, target_lang: tgt }),
      });
      const data = await res.json();
      setTranslated(data.translated || '');
    } catch {
      setTranslated('');
    }
    setLoading(false);
  }, []);

  // Auto-translate with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputText.trim()) { setTranslated(''); return; }
    debounceRef.current = setTimeout(() => {
      doTranslate(inputText, sourceLang, targetLang);
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [inputText, sourceLang, targetLang, doTranslate]);

  const handleSwap = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setInputText(translated);
    setTranslated(inputText);
  };

  const handleCopy = () => {
    if (!translated) return;
    navigator.clipboard.writeText(translated);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSpeak = () => {
    if (!translated) return;
    const utterance = new SpeechSynthesisUtterance(translated);
    const langCode = LANGUAGES.find(l => l.code === targetLang);
    if (langCode) utterance.lang = targetLang.toLowerCase().slice(0, 2);
    speechSynthesis.speak(utterance);
  };

  const handlePhrase = (phrase) => {
    setInputText(phrase);
  };

  const content = (
    <div className="space-y-6" data-testid="translator">
      {/* Header */}
      {!embedded && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-2">
          {onBack && (
            <button onClick={onBack} className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="translator-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white font-outfit flex items-center gap-2.5">
              <Languages className="w-7 h-7 text-teal-400" /> {T.title}
            </h1>
            <p className="text-slate-400 text-sm mt-0.5">{T.subtitle}</p>
          </div>
        </motion.div>
      )}

      {embedded && (
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Languages className="w-5 h-5 text-teal-400" /> {T.title}
          </h2>
          <p className="text-slate-400 text-sm mt-0.5">{T.subtitle}</p>
        </div>
      )}

      {/* Language Selectors */}
      <div className="flex items-end gap-3">
        <LanguageSelect value={sourceLang} onChange={setSourceLang} label={T.from} testId="translator-source-lang" />
        <button onClick={handleSwap}
          className="w-11 h-11 flex-shrink-0 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-slate-400 hover:text-teal-400 hover:bg-teal-500/10 hover:border-teal-500/30 transition-all mb-0.5"
          data-testid="translator-swap">
          <ArrowLeftRight className="w-4 h-4" />
        </button>
        <LanguageSelect value={targetLang} onChange={setTargetLang} label={T.to} testId="translator-target-lang" />
      </div>

      {/* Input / Output */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Source */}
        <div className="relative">
          <textarea ref={inputRef} value={inputText} onChange={e => setInputText(e.target.value)}
            placeholder={T.inputPlaceholder}
            rows={5}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 focus:bg-white/[0.07] transition-all resize-none"
            data-testid="translator-input" />
          {inputText && (
            <button onClick={() => { setInputText(''); setTranslated(''); }}
              className="absolute top-3 right-3 text-slate-500 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
              <span className="text-xs">✕</span>
            </button>
          )}
        </div>

        {/* Target */}
        <div className="relative">
          <div className="w-full bg-teal-500/[0.04] border border-teal-500/10 rounded-2xl px-4 py-3.5 min-h-[134px] text-sm text-teal-50"
            data-testid="translator-output">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">{T.translate}...</span>
              </div>
            ) : translated ? (
              <p className="leading-relaxed whitespace-pre-line">{translated}</p>
            ) : (
              <p className="text-slate-500 text-sm">{T.translate}...</p>
            )}
          </div>
          {translated && (
            <div className="absolute top-3 right-3 flex gap-1">
              <button onClick={handleSpeak}
                className="text-slate-500 hover:text-teal-400 p-1.5 rounded-lg hover:bg-teal-500/10 transition-colors"
                data-testid="translator-speak">
                <Volume2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleCopy}
                className="text-slate-500 hover:text-teal-400 p-1.5 rounded-lg hover:bg-teal-500/10 transition-colors"
                data-testid="translator-copy">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* AI badge */}
      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.06]">
          <Sparkles className="w-3 h-3 text-teal-500" /> {T.powered}
        </span>
      </div>

      {/* Quick Phrases */}
      <div data-testid="translator-phrases">
        <h3 className="text-white font-semibold text-sm mb-3">{T.phrases}</h3>
        
        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mb-3">
          {PHRASE_CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => setActiveCat(activeCat === cat.key ? null : cat.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                activeCat === cat.key
                  ? `${cat.bg} ${cat.color} border-current`
                  : 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10'
              }`}
              data-testid={`phrase-cat-${cat.key}`}>
              <cat.icon className="w-3.5 h-3.5" />
              {catLabels[cat.key]}
            </button>
          ))}
        </div>

        {/* Phrase chips */}
        <AnimatePresence mode="wait">
          {activeCat && (
            <motion.div key={activeCat}
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap gap-2 overflow-hidden">
              {(PHRASE_CATEGORIES.find(c => c.key === activeCat)?.phrases[phraseLang] || []).map((phrase, i) => (
                <button key={i} onClick={() => handlePhrase(phrase)}
                  className="text-xs px-3 py-2 bg-white/[0.04] hover:bg-teal-500/10 border border-white/[0.08] hover:border-teal-500/20 rounded-xl text-slate-300 hover:text-teal-300 transition-all"
                  data-testid={`phrase-${activeCat}-${i}`}>
                  {phrase}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {content}
      </div>
    </div>
  );
}
